import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  addMonths,
  formatDate,
  monthGrid,
  outOfRange,
  parseDate,
  startOfMonth,
  today as todayString,
} from '../../lib/date';
import { cn } from '../../lib/utils';

/**
 * Lưới lịch một tháng — nền của `<DatePicker>`.
 *
 * ## Vì sao tự dựng thay vì `react-day-picker`
 *
 * Không phải vì "tự làm cho vui". Mô hình ngày của ứng dụng là **chuỗi
 * `YYYY-MM-DD` không múi giờ** (`CalendarDate` ở `@apm/contracts`), còn
 * `react-day-picker` làm việc bằng `Date` giờ **địa phương**. Ghép hai thứ đó
 * lại nghĩa là mỗi call site phải chuyển đổi qua lại, và đó đúng là chỗ sinh ra
 * lỗi lệch một ngày mà `lib/date.ts` và `features/gantt/scale.ts` đã ghi lại.
 * Một ô ngày lệch một ngày trông **hoàn toàn bình thường** — không ai phát hiện
 * cho tới khi hạn chót sai.
 *
 * Phần còn lại (tên tháng, tên thứ) lấy từ `Intl`, thứ trình duyệt nào cũng có
 * sẵn — nên không cần `date-fns/locale` chỉ để có chữ "Tháng 9".
 *
 * ## Bàn phím
 *
 * Đúng khuôn `role="grid"` của WAI-ARIA: mũi tên đi từng ngày/tuần, `Home`/`End`
 * về đầu/cuối tuần, `PageUp`/`PageDown` đổi tháng. **Chỉ một ô trong lưới có
 * `tabIndex=0`** (roving tabindex) — nếu 42 ô đều nhận tiêu điểm thì người dùng
 * bàn phím phải bấm Tab 42 lần để thoát khỏi một cái lịch.
 */
export interface CalendarProps {
  /** `YYYY-MM-DD`, hoặc rỗng khi chưa chọn. */
  value?: string | undefined;
  onSelect: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  /** Ghi đè "hôm nay" — test truyền vào để không phụ thuộc đồng hồ máy chạy. */
  now?: string;
  className?: string;
}

export function Calendar({ value, onSelect, min, max, now, className }: CalendarProps): ReactNode {
  const { t, i18n } = useTranslation();
  const today = now ?? todayString();

  /** Ngày đang giữ tiêu điểm bàn phím. Mặc định: giá trị đang chọn, hoặc hôm nay. */
  const [cursor, setCursor] = useState(() => value ?? today);
  const [month, setMonth] = useState(() => startOfMonth(parseDate(value ?? today)));
  const gridRef = useRef<HTMLDivElement>(null);

  const locale = i18n.language === 'en' ? 'en-GB' : 'vi-VN';
  const days = useMemo(() => monthGrid(month), [month]);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        month,
      ),
    [locale, month],
  );

  /**
   * Nhãn đầy đủ cho từng ô — "4 tháng 9, 2026", không phải "4".
   *
   * Lưới luôn 42 ô nên nó chứa cả ngày của tháng bên cạnh: tháng 9/2026 có
   * **hai** ô mang chữ "4". Để nguyên thì trình đọc màn hình đọc "4" hai lần mà
   * không nói tháng nào, và người dùng không có cách nào chọn đúng ô.
   */
  const dayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    [locale],
  );

  /**
   * Tên thứ viết tắt, bắt đầu **thứ Hai**.
   *
   * Lấy từ `Intl` chứ không hardcode: cùng một component phải đọc được ở cả hai
   * ngôn ngữ, và bảng chữ tự viết sẽ lệch khỏi phần còn lại của giao diện ngay
   * lần thêm ngôn ngữ thứ ba.
   */
  const weekdays = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    // 2026-01-05 là một thứ Hai. Mốc bất kỳ, miễn là thứ Hai.
    const monday = parseDate('2026-01-05');
    return Array.from({ length: 7 }, (_, index) => format.format(addDays(monday, index)));
  }, [locale]);

  /**
   * Tiêu điểm phải **theo con trỏ** sang ô mới — nếu không, người dùng bàn phím
   * thấy ô sáng lên ở một chỗ còn phím Enter lại tác động ở chỗ khác.
   *
   * Làm bằng effect chứ không phải `requestAnimationFrame` sau `setState`: rAF
   * chạy ngoài vòng đời React nên nó có thể bắn **trước** khi ô mới kịp nhận
   * `tabIndex=0`, và thứ tự đó không có gì bảo đảm. Cờ ref giới hạn việc dời
   * tiêu điểm cho **riêng thao tác bàn phím** — không có nó thì mỗi lần
   * component vẽ lại là một lần lịch tự giành tiêu điểm khỏi ô đang gõ.
   */
  const grabFocus = useRef(false);

  useEffect(() => {
    if (!grabFocus.current) return;
    grabFocus.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${cursor}"]`)?.focus();
  }, [cursor]);

  const move = (days_: number): void => {
    focus(formatDate(addDays(parseDate(cursor), days_)));
  };

  const focus = (next: string): void => {
    grabFocus.current = true;
    setCursor(next);
    setMonth(startOfMonth(parseDate(next)));
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };

    if (event.key in step) {
      event.preventDefault();
      move(step[event.key]!);
      return;
    }

    const current = parseDate(cursor);
    if (event.key === 'Home') {
      event.preventDefault();
      focus(formatDate(addDays(current, -((current.getUTCDay() + 6) % 7))));
    } else if (event.key === 'End') {
      event.preventDefault();
      focus(formatDate(addDays(current, 6 - ((current.getUTCDay() + 6) % 7))));
    } else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      focus(formatDate(addMonths(current, event.key === 'PageUp' ? -1 : 1)));
    }
  };

  const shiftMonth = (delta: number): void => setMonth(addMonths(month, delta));

  return (
    <div className={cn('w-64 select-none', className)}>
      <div className="mb-2 flex items-center justify-between gap-1">
        <NavButton label={t('calendar.prevMonth')} onClick={() => shiftMonth(-1)}>
          <ChevronLeft className="size-4" aria-hidden />
        </NavButton>

        {/* `aria-live`: đổi tháng bằng bàn phím phải được đọc lên, nếu không
            người dùng screen reader không biết mình vừa đi đâu. */}
        <div aria-live="polite" className="text-sm font-medium capitalize">
          {monthLabel}
        </div>

        <NavButton label={t('calendar.nextMonth')} onClick={() => shiftMonth(1)}>
          <ChevronRight className="size-4" aria-hidden />
        </NavButton>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {weekdays.map((day) => (
          <div
            key={day}
            className="py-1 text-center text-[0.7rem] font-medium text-muted-foreground"
          >
            {day}
          </div>
        ))}
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-label={monthLabel}
        onKeyDown={onKeyDown}
        className="grid grid-cols-7 gap-0.5"
      >
        {days.map((date) => {
          const iso = formatDate(date);
          const outside = date.getUTCMonth() !== month.getUTCMonth();
          const disabled = outOfRange(iso, min, max);
          const selected = iso === value;

          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              data-day={iso}
              disabled={disabled}
              aria-label={dayLabel.format(date)}
              aria-selected={selected}
              aria-current={iso === today ? 'date' : undefined}
              // Roving tabindex — xem chú thích ở đầu file.
              tabIndex={iso === cursor ? 0 : -1}
              onClick={() => {
                setCursor(iso);
                onSelect(iso);
              }}
              className={cn(
                'relative h-8 rounded-DEFAULT text-sm tabular-nums transition-colors',
                'hover:bg-muted disabled:pointer-events-none disabled:opacity-30',
                // Ngày của tháng bên cạnh vẫn bấm được — mờ đi để không tranh
                // chú ý, chứ không ẩn: ẩn thì lưới thủng lỗ chỗ.
                outside && 'text-muted-foreground/60',
                selected && 'bg-primary font-medium text-primary-foreground hover:bg-primary/90',
              )}
            >
              {date.getUTCDate()}
              {/* Chấm "hôm nay". Không dùng màu làm kênh duy nhất: ô đang chọn
                  đã đổi nền, còn ô hôm nay có thêm dấu chấm nhìn thấy được kể
                  cả khi nó cũng đang được chọn. */}
              {iso === today && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full',
                    selected ? 'bg-primary-foreground' : 'bg-primary',
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-7 items-center justify-center text-muted-foreground hover:text-foreground rounded-full glass-hover"
    >
      {children}
    </button>
  );
}
